import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layers, ChevronRight, Loader2, TrendingUp, RotateCw, TrendingDown, Sparkles } from 'lucide-react';
import api from '../utils/api';

/**
 * Pattern Index — a 4-week timeline of named returning/shifting patterns.
 * Sits on the Navigator page below the weekly block. Makes longitudinal
 * change visually undeniable: the user sees their own becoming.
 */
const directionStyles = {
  returning: { icon: RotateCw, color: 'text-amber-300', bg: 'bg-amber-500/10', border: 'border-amber-500/30', labelEn: 'Returning', labelTr: 'Geri Dönen' },
  deepening: { icon: TrendingUp, color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10', border: 'border-[#D4AF37]/30', labelEn: 'Deepening', labelTr: 'Derinleşen' },
  fading: { icon: TrendingDown, color: 'text-blue-300', bg: 'bg-blue-500/10', border: 'border-blue-500/30', labelEn: 'Fading', labelTr: 'Sönen' },
  new: { icon: Sparkles, color: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', labelEn: 'New', labelTr: 'Yeni' },
};

const PatternIndex = ({ language = 'en' }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/patterns/index');
        if (mounted) setData(res.data);
      } catch (e) {
        if (mounted) setData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="glass-panel rounded-2xl p-5 flex items-center justify-center mt-6">
        <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!data?.ready || !data?.patterns?.length) {
    return (
      <div className="glass-panel rounded-2xl p-5 mt-6" data-testid="pattern-index-empty">
        <div className="flex items-center gap-2 mb-2">
          <Layers className="w-4 h-4 text-[#D4AF37]/70" />
          <h3 className="text-xs uppercase tracking-[0.25em] text-[#6B7280] font-sans">
            {language === 'tr' ? 'Desen Dizini' : 'Pattern Index'}
          </h3>
        </div>
        <p className="text-[#A1A1AA] text-sm font-sans">
          {language === 'tr'
            ? 'Birkaç haftalık tutarlılık sonrasında desenler burada görünmeye başlayacak.'
            : 'Patterns will surface here after a few weeks of consistent tracking.'}
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mt-8"
      data-testid="pattern-index"
    >
      <div className="flex items-center gap-2 mb-3 px-1">
        <Layers className="w-4 h-4 text-[#D4AF37]" />
        <h3 className="text-xs uppercase tracking-[0.25em] text-[#A1A1AA] font-sans">
          {language === 'tr' ? 'Desen Dizini' : 'Pattern Index'}
        </h3>
        <span className="text-[10px] text-[#6B7280] font-sans ml-1">
          {language === 'tr' ? `Son ${data.weeks_covered} hafta` : `Last ${data.weeks_covered} weeks`}
        </span>
      </div>

      <ul className="space-y-2" data-testid="pattern-index-list">
        {data.patterns.map((p, idx) => {
          const style = directionStyles[p.direction] || directionStyles.returning;
          const Icon = style.icon;
          const isOpen = expanded === idx;
          return (
            <li
              key={idx}
              className={`rounded-xl border transition-all ${style.border} ${style.bg}`}
              data-testid={`pattern-item-${idx}`}
            >
              <button
                onClick={() => setExpanded(isOpen ? null : idx)}
                className="w-full p-3 flex items-center gap-3 text-left"
              >
                <div className={`w-8 h-8 rounded-full bg-black/30 flex items-center justify-center shrink-0`}>
                  <Icon className={`w-4 h-4 ${style.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#F8F5F2] font-serif text-base leading-snug">
                    {p.label}
                  </p>
                  <p className={`text-[10px] uppercase tracking-wider mt-0.5 font-sans ${style.color}`}>
                    {language === 'tr' ? style.labelTr : style.labelEn}
                    {p.weeks?.length > 0 && ` · ${p.weeks.length}w`}
                  </p>
                </div>
                <ChevronRight className={`w-4 h-4 text-[#6B7280] transition-transform ${isOpen ? 'rotate-90' : ''}`} />
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 pb-3 ml-11 space-y-2">
                      {p.integration && (
                        <p className="text-[#D1D1D6] font-sans text-xs italic leading-relaxed border-l-2 border-[#D4AF37]/30 pl-3">
                          {p.integration}
                        </p>
                      )}
                      {p.weeks?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {p.weeks.map((w, i) => (
                            <span
                              key={i}
                              className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[#A1A1AA] font-sans"
                            >
                              {w}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
};

export default PatternIndex;
